import type {
  ClientPresentationNode,
  ClientPresentationSourceReference,
  ReportContentBlock,
  ReportSection,
  SectionFourMethodologyCopy,
} from "./domain";
import {
  approveClientSectionPresentation,
  clientPresentationSourceSignature,
  createClientPresentationDraft,
} from "./client-presentation";
import { defaultEvidenceCaption, sectionOneClientDraft, sectionOneClientImages } from "./section1-client-output";
import { sectionTwoClientImages, sectionTwoClientLinks } from "./section2-client-output";
import { sectionThreeClientImages, sectionThreeClientSections, sectionThreeClientWebsite } from "./section3-client-output";
import { mapsLinkLabel } from "./section2-interpretation";
import {
  labelledMappingFromPresentation,
  mappedText,
  mappingFor,
  numberedActionMapping,
  paragraphMappingFromPresentation,
  presentationSourceValue,
  reviewedPresentationMapping,
  semanticListMappingFromLabelled,
} from "./presentation-mapping";

export const PROMPT_TESTING_METHODOLOGY_TEMPLATE = {
  id: "studio-prompt-testing-methodology",
  version: "1.1",
  title: "How the prompt testing works",
  content: `The prompts in this audit are designed to reflect believable commercial searches from people looking for relevant services. They test whether the business is understood, considered or recommended when the search closely matches its services, customers and location.

These are synthetic prompts. They do not include the personal history, preferences or accumulated context that a real user may have built through previous conversations with an AI system. Different users may therefore receive different answers.

The results should be treated as a structured indication of current visibility, not a guarantee of how every search or recommendation will appear.

The purpose is not to test whether the business appears everywhere, but whether it appears where it should for commercially relevant searches.`,
} as const;

const placeholderMethodology = /^\s*remove this\s*$/i;
const trim = (value: string | undefined) => value?.trim() ?? "";
const source = (kind: "field" | "structured", key: string):ClientPresentationSourceReference[] => [{ kind, key }];

function paragraphs(id: string, value: string, sourceKey: string): ClientPresentationNode[] {
  return value.replace(/\r\n?/g, "\n").split(/\n{2,}/).map(item => item.trim()).filter(Boolean).map((text, index) => ({
    id: `${id}-${index + 1}`,
    type: "paragraph",
    text,
    sourceReferences: source("field", sourceKey),
  }));
}

function heading(id: string, text: string, level: 3 | 4 = 3): ClientPresentationNode {
  return { id, type: "heading", level, text };
}

function mappedParagraphs(section:ReportSection,id:string,value:string,sourceKey:string){
  const resolved=mappingFor(section,sourceKey,"paragraph-boundaries");
  return resolved?resolved.mapping.spans.map((span,index):ClientPresentationNode=>({
    id:`${id}-${index+1}`,type:"paragraph",text:mappedText(span,resolved.source),sourceReferences:source("field",sourceKey),
  })):paragraphs(id,value,sourceKey);
}

function parseSectionOneSources(value: string): ClientPresentationNode[] {
  const recognisedInlineSourceList =
    /^The strongest visible sources appear to be:/i.test(value.trim()) &&
    ((value.match(/\s+-\s+/g) ?? []).length >= 2 ||
      (value.match(/(?:Ã¢â‚¬Â¢|â€¢|•)\s*/g) ?? []).length >= 2);

  if (!recognisedInlineSourceList) {
    return paragraphs("section1-sources", value, "clientDraft.sources");
  }

  const repaired = value
    .replace(/(?:Ã¢â‚¬Â¢|â€¢|•)\s*/g, "- ")
    .replace(/\s+-\s+/g, "\n- ");
  const lines = repaired.split("\n").map(item => item.trim()).filter(Boolean);
  const introduction = lines[0]?.replace(/-\s*$/, "").trim() ?? "";
  const list: string[] = [];
  let trailing = "";
  for (const line of lines.slice(1)) {
    if (line.startsWith("- ")) {
      const item = line.slice(2);
      const boundary = item.search(/\s+(?:These sources|The available evidence)\b/);
      if (boundary >= 0) {
        list.push(item.slice(0, boundary).trim());
        trailing = item.slice(boundary).trim();
      } else list.push(item);
    } else trailing = [trailing, line].filter(Boolean).join(" ");
  }
  if (list.length < 2) return paragraphs("section1-sources", value, "sectionOne.clientDraft.sources");
  return [
    ...(introduction ? paragraphs("section1-sources-introduction", introduction, "sectionOne.clientDraft.sources") : []),
    { id: "section1-sources-list", type: "unordered-list", items: list, sourceReferences: source("field", "sectionOne.clientDraft.sources") },
    ...(trailing ? paragraphs("section1-sources-follow-up", trailing, "sectionOne.clientDraft.sources") : []),
  ];
}

function sectionOneNodes(section: ReportSection, organisationName: string): ClientPresentationNode[] {
  const draft = sectionOneClientDraft(section);
  if (!draft) return [];
  const images = sectionOneClientImages(section);
  return [
    heading("section1-introduction-heading", "Introduction"),
    ...paragraphs("section1-introduction", draft.introduction, "sectionOne.clientDraft.introduction"),
    {
      id:"section1-understanding",type:"callout",title:"What Google currently understands",tone:"teal",
      children:mappedParagraphs(section,"section1-understanding",draft.currentUnderstanding,"sectionOne.clientDraft.currentUnderstanding"),
    },
    {
      id:"section1-inaccuracies",type:"callout",title:"What Google has got wrong or confused",tone:"gold",
      children:mappedParagraphs(section,"section1-inaccuracies",draft.inaccuracies,"sectionOne.clientDraft.inaccuracies"),
    },
    heading("section1-sources-heading", "Sources shaping Google’s understanding"),
    {
      id:"section1-evidence",
      type:"card-group",
      tone:"neutral",
      children:[
        ...parseSectionOneSources(draft.sources),
        ...(images.length ? [{
      id: "section1-supporting-evidence",
      type: "image-group" as const,
      title: "Supporting evidence",
      children: images.map((item, index): ClientPresentationNode => ({
        id: `section1-evidence-${index + 1}`,
        type: "screenshot",
        imageFileKey: item.fileKey,
        alternativeText: item.alternativeText || item.sourceTitle,
        caption: defaultEvidenceCaption(item, organisationName),
        layout: item.imageLayout ?? "Automatic",
        sourceReferences: source("structured", `sectionOne.sourceEvidence.${item.id}`),
      })),
        }] : []),
      ],
    },
    {
      id: "section1-next-step",
      type: "next-action",
      title: "What we will test next",
      text: draft.nextStep,
      tone: "teal",
      sourceReferences: source("field", "sectionOne.clientDraft.nextStep"),
    },
  ];
}

function recommendationItems(value: string) {
  return value.match(/[^.!?]+[.!?]+(?:\s+|$)|[^.!?]+$/g)?.map(item => item.trim()).filter(Boolean) ?? [];
}

function sectionTwoNodes(section: ReportSection): ClientPresentationNode[] {
  const draft = section.sectionTwoWorkflow?.clientDraft;
  if (!draft) return [];
  const images = sectionTwoClientImages(section);
  const links = sectionTwoClientLinks(section);
  const recommendations = recommendationItems(draft.recommendedChecks);
  return [
    {
      id: "section2-current-state",
      type: "card-group",
      title: "Current profile state",
      children: [
        { id: "section2-introduction", type: "labelled-item", label: "Introduction", text: draft.introduction, sourceReferences: source("field", "sectionTwo.clientDraft.introduction") },
        { id: "section2-profile-status", type: "labelled-item", label: "Google Business Profile", text: draft.profileStatus, sourceReferences: source("field", "sectionTwo.clientDraft.profileStatus") },
      ],
    },
    {
      id: "section2-outdated-information",
      type: "callout",
      title: "Incorrect or outdated information",
      text: [draft.mapsTest, draft.profileCompleteness].filter(Boolean).join("\n\n"),
      tone: "gold",
      sourceReferences: source("field", "sectionTwo.clientDraft.mapsTest"),
    },
    {
      id: "section2-commercial-consequence",
      type: "card-group",
      title: "Commercial consequence",
      children: [
        { id: "section2-working-well", type: "labelled-item", label: "What is working well", text: draft.workingWell, sourceReferences: source("field", "sectionTwo.clientDraft.workingWell") },
        { id: "section2-needs-attention", type: "labelled-item", label: "What needs attention", text: draft.needsAttention, sourceReferences: source("field", "sectionTwo.clientDraft.needsAttention") },
      ],
    },
    {
      id: "section2-recommended-correction",
      type: "card",
      title: "Recommended correction",
      children: recommendations.length > 1 ? [{
        id: "section2-recommendations-list",
        type: "unordered-list",
        items: recommendations,
        sourceReferences: source("field", "sectionTwo.clientDraft.recommendedChecks"),
      }] : paragraphs("section2-recommendations", draft.recommendedChecks, "sectionTwo.clientDraft.recommendedChecks"),
    },
    ...(links.length ? [{
      id: "section2-profile-links",
      type: "card-group" as const,
      title: "Profile evidence",
      children: links.map((item, index): ClientPresentationNode => ({
        id: `section2-profile-link-${index + 1}`,
        type: "labelled-item",
        label: mapsLinkLabel(item.linkType),
        text: item.url,
        url: item.url,
        sourceReferences: source("structured", `sectionTwo.links.${item.id}`),
      })),
    }] : []),
    ...(draft.evidenceLimitations ? [{
      id: "section2-evidence-limitations",
      type: "limitation" as const,
      title: "Evidence limitations",
      text: draft.evidenceLimitations,
      tone: "gold" as const,
      sourceReferences: source("field", "sectionTwo.clientDraft.evidenceLimitations"),
    }] : []),
    ...(images.length ? [{
      id: "section2-supporting-screenshots",
      type: "image-group" as const,
      title: "Supporting screenshots",
      children: images.map((item, index): ClientPresentationNode => ({
        id: `section2-screenshot-${index + 1}`,
        type: "screenshot",
        imageFileKey: item.fileKey,
        alternativeText: item.alternativeText || item.sourceTitle,
        caption: item.selectedExcerpt,
        layout: item.imageLayout ?? "Automatic",
        sourceReferences: source("structured", `sectionTwo.screenshots.${item.id}`),
      })),
    }] : []),
    ...(draft.nextStep ? [{
      id: "section2-next-step",
      type: "next-action" as const,
      title: "What we will examine next",
      text: draft.nextStep,
      tone: "teal" as const,
      sourceReferences: source("field", "sectionTwo.clientDraft.nextStep"),
    }] : []),
  ];
}

function splitLabelledText(value: string, labels: readonly string[]) {
  const positions = labels.map(label => ({ label, index: value.indexOf(label) })).filter(item => item.index >= 0).sort((a, b) => a.index - b.index);
  if (!positions.length) return [{ label: "Website assessment", text: value }];
  return positions.map((item, index) => ({
    label: item.label,
    text: value.slice(item.index + item.label.length, positions[index + 1]?.index ?? value.length).trim(),
  })).filter(item => item.text);
}

function technicalFindingNodes(section: ReportSection, value: string): ClientPresentationNode[] {
  const titles = (section.sectionThreeWorkflow?.technicalReviewItems ?? []).filter(item => item.includeForClient)
    .map(item => item.title).filter(title => value.includes(title));
  const parts = splitLabelledText(value, titles);
  if (!titles.length || parts.length < 2) return paragraphs("section3-technical", value, "sectionThree.clientDraft.ayzeoTechnicalFindings");
  return parts.map((item, index) => ({
    id: `section3-technical-${index + 1}`,
    type: "labelled-item",
    label: item.label,
    text: item.text,
    note: "Reported by Ayzeo",
    sourceReferences: source("field", "sectionThree.clientDraft.ayzeoTechnicalFindings"),
  }));
}

function sectionThreeNodes(section: ReportSection): ClientPresentationNode[] {
  const draft = section.sectionThreeWorkflow?.clientDraft;
  if (!draft) return [];
  const websiteAssessment = draft.websiteAssessment ?? sectionThreeClientSections(section).map(([, text]) => text).join("\n\n");
  const mapped=mappingFor(section,"sectionThree.clientDraft.websiteAssessment","semantic-list")??
    mappingFor(section,"sectionThree.clientDraft.websiteAssessment","paragraph-boundaries");
  const nodes: ClientPresentationNode[] = [];
  let lastGroup = "";
  if(mapped){
    const spans=mapped.mapping.spans;
    for(let index=0;index<spans.length;){
      const part=spans[index],group=part.group||"Website assessment",label=part.label||"Website assessment";
      if(group!==lastGroup){nodes.push(heading(`section3-group-${index+1}`,group));lastGroup=group}
      if(part.role==="list-item"){
        const items:string[]=[];
        let cursor=index;
        while(cursor<spans.length&&spans[cursor].role==="list-item"&&spans[cursor].label===label){
          items.push(mappedText(spans[cursor],mapped.source));cursor+=1;
        }
        nodes.push({
          id:`section3-assessment-${index+1}`,type:"card",title:label,
          children:[{id:`section3-list-${index+1}`,type:"unordered-list",items,sourceReferences:source("field","sectionThree.clientDraft.websiteAssessment")}],
          sourceReferences:source("field","sectionThree.clientDraft.websiteAssessment"),
        });
        index=cursor;
      }else{
        nodes.push({id:`section3-assessment-${index+1}`,type:"card",title:label,text:mappedText(part,mapped.source),sourceReferences:source("field","sectionThree.clientDraft.websiteAssessment")});
        index+=1;
      }
    }
  }else{
    nodes.push(heading("section3-group-1","Website assessment"),{
      id:"section3-assessment-1",type:"card",text:websiteAssessment,
      sourceReferences:source("field","sectionThree.clientDraft.websiteAssessment"),
    });
  }
  const technical = trim(draft.ayzeoTechnicalFindings ?? draft.technicalFindings);
  if (technical) nodes.push(
    heading("section3-technical-heading", "Technical foundations"),
    {
      id: "section3-technical-findings",
      type: "card-group",
      title: "Technical findings reported by Ayzeo",
      children: technicalFindingNodes(section, technical),
    },
  );
  const actions = trim(draft.recommendedWebsiteActions ?? draft.recommendedChanges);
  const actionMapping=mappingFor(section,"sectionThree.clientDraft.recommendedWebsiteActions","numbered-actions");
  if (actions) nodes.push(
    heading("section3-actions-heading", "Recommended website actions"),
    {
      id: "section3-actions",
      type: actionMapping?"card-group":"card",
      text: actionMapping?undefined:actions,
      children:actionMapping?actionMapping.mapping.spans.map((span,index)=>{
        const full=mappedText(span,actionMapping.source);
        const title=span.headingReviewState==="Reviewed"&&span.headingStart!==undefined&&span.headingEnd!==undefined
          ?actionMapping.source.slice(span.headingStart,span.headingEnd):undefined;
        const detail=title?full.replace(title,"").replace(/^\s*(?::|[–—])?\s*/,""):full;
        return{id:`section3-action-${index+1}`,type:"card" as const,label:`${index+1}`,title:title?`${index+1}. ${title}`:`Action ${index+1}`,text:detail||undefined,sourceReferences:source("field","sectionThree.clientDraft.recommendedWebsiteActions")};
      }):undefined,
      sourceReferences: source("field", "sectionThree.clientDraft.recommendedWebsiteActions"),
    },
  );
  const limitations = trim(draft.evidenceLimitations);
  if (limitations) nodes.push({
    id: "section3-limitations",
    type: "limitation",
    title: "Evidence limitations",
    text: limitations,
    tone: "gold",
    sourceReferences: source("field", "sectionThree.clientDraft.evidenceLimitations"),
  });
  const website = sectionThreeClientWebsite(section);
  if (website) nodes.push({
    id: "section3-website-reviewed",
    type: "labelled-item",
    label: "Website reviewed",
    text: "Visit the website reviewed",
    url: website.url,
    sourceReferences: source("structured", "sectionThree.website"),
  });
  const images = sectionThreeClientImages(section);
  if (images.length) nodes.push({
    id: "section3-supporting-screenshots",
    type: "image-group",
    title: "Supporting screenshots",
    children: images.map((item, index): ClientPresentationNode => ({
      id: `section3-screenshot-${index + 1}`,
      type: "screenshot",
      imageFileKey: item.fileKey,
      alternativeText: item.alternativeText || item.sourceTitle,
      caption: item.selectedExcerpt,
      layout: item.imageLayout ?? "Automatic",
      sourceReferences: source("structured", `sectionThree.screenshots.${item.id}`),
    })),
  });
  if (trim(draft.nextStep)) nodes.push({
    id: "section3-next-step",
    type: "next-action",
    title: "What we will examine next",
    text: draft.nextStep,
    tone: "teal",
    sourceReferences: source("field", "sectionThree.clientDraft.nextStep"),
  });
  return nodes;
}

function methodologyCopy(copiedAt: string, existing?: SectionFourMethodologyCopy): SectionFourMethodologyCopy {
  if (existing && !placeholderMethodology.test(existing.content)) return existing;
  return {
    templateId: PROMPT_TESTING_METHODOLOGY_TEMPLATE.id,
    templateVersion: PROMPT_TESTING_METHODOLOGY_TEMPLATE.version,
    title: PROMPT_TESTING_METHODOLOGY_TEMPLATE.title,
    content: PROMPT_TESTING_METHODOLOGY_TEMPLATE.content,
    copiedAt,
  };
}

function ensurePresentationMappings(section:ReportSection,updatedAt:string,updatedBy:string){
  const existing=[...(section.presentationMappings??[])];
  const add=(mapping:ReturnType<typeof paragraphMappingFromPresentation>|undefined)=>{
    if(mapping&&!existing.some(item=>item.id===mapping.id))existing.push(mapping);
  };
  const priorNodes=section.clientPresentation?.nodes??[];
  if(section.id==="google-understanding"){
    for(const key of["sectionOne.clientDraft.currentUnderstanding","sectionOne.clientDraft.inaccuracies"]){
      const value=presentationSourceValue(section,key);
      if(value)add(paragraphMappingFromPresentation(value,key,priorNodes,updatedAt,updatedBy,`${section.id}-${key}-paragraphs`));
    }
  }
  if(section.id==="website-review"){
    const key="sectionThree.clientDraft.websiteAssessment",value=presentationSourceValue(section,key);
    if(value){
      const base=labelledMappingFromPresentation(value,key,priorNodes,updatedAt,updatedBy,`${section.id}-page-boundaries`);
      add(base);
      if(base)add(semanticListMappingFromLabelled(base,value,["Homepage","Blog"],`${section.id}-reviewed-lists`,updatedAt,updatedBy));
    }
    const actionKey="sectionThree.clientDraft.recommendedWebsiteActions",actions=presentationSourceValue(section,actionKey);
    if(actions)add(numberedActionMapping(`${section.id}-numbered-actions`,actionKey,actions,updatedAt,updatedBy));
  }
  if(section.id==="prompt-testing"){
    const key=section.sectionFourWorkflow?.promptTestingMethodology?.content
      ?"sectionFour.promptTestingMethodology.content":"sectionFour.clientDraft.promptTestingExplanation";
    const value=presentationSourceValue(section,key);
    if(value&&!existing.some(item=>item.sourceFieldKey===key&&item.kind==="paragraph-boundaries")){
      const raw=value.split(/\n{2,}/).map(text=>({text,start:value.indexOf(text)})).filter(item=>item.text);
      const purpose=value.indexOf("The purpose is not to test");
      const starts=[...new Set([...raw.map(item=>item.start),...(purpose>0?[purpose]:[])])].sort((a,b)=>a-b);
      const spans=starts.map((start,index)=>({start:index===0?0:start,end:index===starts.length-1?value.length:starts[index+1],role:"paragraph" as const}));
      if(spans.length===4)add(reviewedPresentationMapping(`${section.id}-methodology-paragraphs`,"paragraph-boundaries",key,value,spans,updatedAt,updatedBy));
    }
  }
  return existing.length?{...section,presentationMappings:existing}:section;
}

function sectionFourNodes(section: ReportSection): ClientPresentationNode[] {
  const workflow = section.sectionFourWorkflow;
  const draft = workflow?.clientDraft;
  if (!draft) return [];
  const methodology = workflow.promptTestingMethodology?.content || draft.promptTestingExplanation || "";
  const fields: Array<[string, string, string]> = [
    ["Introduction", draft.introduction, "introduction"],
    ["How the prompt testing works", methodology, "promptTestingExplanation"],
    ["How the prompts were tested", draft.testingSummary, "testingSummary"],
    ["Strongest results", draft.strongestResults, "strongestResults"],
    ["Visibility gaps", draft.visibilityGaps, "visibilityGaps"],
    ["Platform comparison", draft.platformComparison, "platformComparison"],
    ["What this means commercially", draft.commercialMeaning, "commercialMeaning"],
    ["Evidence limitations", draft.limitations, "limitations"],
  ];
  return fields.flatMap(([title, value, key], index): ClientPresentationNode[] => {
    if (!trim(value) || placeholderMethodology.test(value)) return [];
    const sourceKey=title==="How the prompt testing works"
      ?(workflow?.promptTestingMethodology?.content?"sectionFour.promptTestingMethodology.content":"sectionFour.clientDraft.promptTestingExplanation")
      :`sectionFour.clientDraft.${key}`;
    const content=title==="How the prompt testing works"
      ?mappedParagraphs(section,`section4-${key}`,value,sourceKey)
      :paragraphs(`section4-${key}`,value,sourceKey);
    if (title === "Evidence limitations") return [{
      id: "section4-limitations",
      type: "limitation",
      title,
      tone: "gold",
      children: content,
      sourceReferences: source("field", `sectionFour.clientDraft.${key}`),
    }];
    if (title === "How the prompt testing works") return [{
      id: "section4-methodology",
      type: "callout",
      title,
      tone: "teal",
      children: content,
      sourceReferences: source("structured", "sectionFour.promptTestingMethodology"),
    }];
    return [heading(`section4-heading-${index + 1}`, title), ...content];
  });
}

export function clientPresentationNodes(section: ReportSection, organisationName = "") {
  if (section.id === "google-understanding") return sectionOneNodes(section, organisationName);
  if (section.id === "maps-profile") return sectionTwoNodes(section);
  if (section.id === "website-review") return sectionThreeNodes(section);
  if (section.id === "prompt-testing") return sectionFourNodes(section);
  return [];
}

export function sectionFourPresentationBlocks(section: ReportSection): ReportContentBlock[] {
  const draft=section.sectionFourWorkflow?.clientDraft;
  if(!draft)return section.blocks??[];
  const methodology=section.sectionFourWorkflow?.promptTestingMethodology?.content||draft.promptTestingExplanation||"";
  return [
    ["Introduction",draft.introduction],
    ["How the prompt testing works",methodology],
    ["How the prompts were tested",draft.testingSummary],
    ["Strongest results",draft.strongestResults],
    ["Visibility gaps",draft.visibilityGaps],
    ["Platform comparison",draft.platformComparison],
    ["What this means commercially",draft.commercialMeaning],
    ["Evidence limitations",draft.limitations],
  ].flatMap(([title,content],index)=>trim(content)&&!placeholderMethodology.test(content)?[{
    id:`section4-client-${index}`,
    type:index===0?"summary" as const:"paragraph" as const,
    title,
    content,
    clientVisible:true,
    includeInPdf:true,
    evidenceIds:[],
  }]:[]);
}

export function prepareSectionClientPresentation(
  section: ReportSection,
  updatedAt: string,
  updatedBy: string,
  organisationName = "",
) {
  let sourceSection = section;
  if (section.id === "prompt-testing" && section.sectionFourWorkflow?.clientDraft) {
    const current = section.sectionFourWorkflow.clientDraft.promptTestingExplanation;
    const baseCopy = methodologyCopy(updatedAt, section.sectionFourWorkflow.promptTestingMethodology);
    const copy = current && !placeholderMethodology.test(current) ? {...baseCopy,content:current} : baseCopy;
    sourceSection = {
      ...section,
      sectionFourWorkflow: {
        ...section.sectionFourWorkflow,
        clientDraft: {
          ...section.sectionFourWorkflow.clientDraft,
          promptTestingExplanation: !current || placeholderMethodology.test(current) ? copy.content : current,
        },
        promptTestingMethodology: copy,
        promptTestingExplanationApproved: placeholderMethodology.test(current ?? "") ? false : section.sectionFourWorkflow.promptTestingExplanationApproved,
        promptTestingExplanationApprovedAt: placeholderMethodology.test(current ?? "") ? undefined : section.sectionFourWorkflow.promptTestingExplanationApprovedAt,
      },
    };
  }
  sourceSection=ensurePresentationMappings(sourceSection,updatedAt,updatedBy);
  const nodes = clientPresentationNodes(sourceSection, organisationName);
  return nodes.length ? createClientPresentationDraft(sourceSection, nodes, updatedAt, updatedBy) : sourceSection;
}

export function approvePreparedClientPresentation(section: ReportSection, approvedAt: string, approvedBy: string, organisationName = "") {
  const prepared = prepareSectionClientPresentation({ ...section, clientPresentation: undefined }, approvedAt, approvedBy, organisationName);
  return approveClientSectionPresentation(prepared, approvedAt, approvedBy);
}

export function presentationIsCurrent(section: ReportSection) {
  return section.clientPresentation?.sourceContentSignature === clientPresentationSourceSignature(section);
}
